import { Checkbox } from '../../../../../../widgets/checkbox';
import { FormControl } from '../../../../../../widgets/formControl';
import React from 'react';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerAreaNode, ComposerTemplateNode, ComposerTemplateSideRestrictions } from '../attributes/composerAttributesModels';
import Stack from '../../../../../../widgets/stack/stack';

interface ComposerTemplateEditorProps {
    template: ComposerTemplateNode;
    area: ComposerAreaNode;
}

const Container = styled.div`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 12px;
    padding: 12px;
    border-right: 1px solid ${theme.app.borderColor};
    overflow: auto;
`;

export const ComposerTemplateEditor: React.FC<ComposerTemplateEditorProps> = ({ template, area }) => {
    const [updateTemplateRestriction] = useComposerStore((state) => [state.updateTemplateRestriction]);
    const restrictions = template.restrictions.get(area.sideID);
    const onPropertyChange = (property: keyof ComposerTemplateSideRestrictions, value: any) => {
        updateTemplateRestriction(template, area.sideID, property, value);
    };

    return (
        <Container>
            <h3>{T._('Template Settings', 'Admin')}</h3>
            <Stack>
                <FormControl label={T._('User can add text', 'Admin')}>
                    <Checkbox
                        checked={restrictions?.userCanAddTexts ?? false}
                        onChange={() => onPropertyChange('userCanAddTexts', !restrictions?.userCanAddTexts)}
                    />
                </FormControl>
                <FormControl label={T._('User can add images', 'Admin')}>
                    <Checkbox
                        checked={restrictions?.userCanAddImages ?? false}
                        onChange={() => onPropertyChange('userCanAddImages', !restrictions?.userCanAddImages)}
                    />
                </FormControl>
                <FormControl label={T._('Gallery enabled', 'Admin')}>
                    <Checkbox
                        checked={restrictions?.isGalleryEnabled ?? false}
                        onChange={() => onPropertyChange('isGalleryEnabled', !restrictions?.isGalleryEnabled)}
                    />
                </FormControl>
                <FormControl label={T._('User image upload enabled', 'Admin')}>
                    <Checkbox
                        checked={restrictions?.userCanAddUploadImages ?? false}
                        onChange={() => onPropertyChange('userCanAddUploadImages', !restrictions?.userCanAddUploadImages)}
                    />
                    {restrictions?.userCanAddUploadImages && (
                        <>
                            <Stack>
                                <FormControl label={T._('JPG Extension', 'Admin')}>
                                    <Checkbox
                                        checked={restrictions?.jpgUploadEnabled ?? false}
                                        onChange={() => onPropertyChange('jpgUploadEnabled', !restrictions?.jpgUploadEnabled)}
                                    />
                                </FormControl>
                                <FormControl label={T._('PNG Extension', 'Admin')}>
                                    <Checkbox
                                        checked={restrictions?.pngUploadEnabled ?? false}
                                        onChange={() => onPropertyChange('pngUploadEnabled', !restrictions.pngUploadEnabled)}
                                    />
                                </FormControl>
                                <FormControl label={T._('SVG Extension', 'Admin')}>
                                    <Checkbox
                                        checked={restrictions?.svgUploadEnabled ?? false}
                                        onChange={() => onPropertyChange('svgUploadEnabled', !restrictions?.svgUploadEnabled)}
                                    />
                                </FormControl>
                                <FormControl label={T._('PDF Extension', 'Admin')}>
                                    <Checkbox
                                        checked={restrictions.pdfUploadEnabled ?? false}
                                        onChange={() => onPropertyChange('pdfUploadEnabled', !restrictions.pdfUploadEnabled)}
                                    />

                                    <FormControl label={T._('PDF Rasterize', 'Admin')}>
                                        <Checkbox
                                            checked={restrictions?.pdfWithRasterizationEnabled ?? false}
                                            onChange={() => onPropertyChange('pdfWithRasterizationEnabled', !restrictions.pdfWithRasterizationEnabled)}
                                        />
                                    </FormControl>
                                </FormControl>
                                <FormControl label={T._('EPS Extension', 'Admin')}>
                                    <Checkbox
                                        checked={restrictions?.epsUploadEnabled ?? false}
                                        onChange={() => onPropertyChange('epsUploadEnabled', !restrictions?.epsUploadEnabled)}
                                    />
                                </FormControl>
                            </Stack>
                        </>
                    )}
                </FormControl>
            </Stack>
        </Container>
    );
};
